<!-- eslint-disable-line vue/multi-word-component-names -->
<template>
  <div class="relative h-full w-full">
    <div
      v-show="state.logs_visible"
      id="caverns_battle_log"
      ref="caverns_battle_log"
      class="max-h-[100%] p-3 overflow-y-auto pointer-events-auto text-xs text-slate-400"
      @scroll="onScroll"
    >
      <BattleLogEntry
        v-if="state.logs_visible"
        v-for="entry in state.log_entries"
        :entry_data="entry"
        :key="entry.entry_id"
        class="w-full text-center"
      />
    </div>
    <div
      class="absolute top-0 left-0 right-0 h-[25px] pointer-events-none bg-gradient-to-b from-black to-transparent z-10"
      aria-hidden="true"
    />
    <button
      class="absolute bottom-2 right-2 z-20 p-2 bg-zinc-800/80 hover:bg-zinc-700/80 rounded-md transition-colors pointer-events-auto"
      @click="toggleLogsVisibility"
    >
      <Eye v-if="!state.logs_visible" :size="20" class="text-zinc-300" />
      <EyeOff v-else :size="20" class="text-zinc-300" />
    </button>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, onUpdated, reactive, ref } from 'vue';
import { Eye, EyeOff } from 'lucide-vue-next';
import { CavernsStore } from '~/flux/stores';
import {
  getBattleEventLogEntry,
  getBattleEventLogEntryForAbility,
  getBattleEventLogEntryForCondition,
  getCavernsNewTurnMessage,
  getBrowserLanguage,
} from '~/text';

const browserLanguage = getBrowserLanguage();
import BattleLogEntry from './BattleLogEntry.vue';

let _entry_queue = [];
let _entry_queue_interval;
let _is_scrolling = false;
let _last_ai_unit_id = null;
let _mouse_is_down = false;

// Batching for inspired conditions
let _inspired_batches = new Map(); // Map<instigatorId, { instigatorName, ownerNames: string[], timeoutId }>
const INSPIRED_BATCH_DELAY = 1000; // 1 second

// Batching for heal events
let _heal_batches = new Map(); // Map<potency, { potency: number, victimNames: string[], timeoutId }>
const HEAL_BATCH_DELAY = 1000; // 1 second

const STORAGE_KEY = 'caverns_battle_log_visible';

// Initialize logs_visible from localStorage, default to true
const getInitialLogsVisible = () => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored !== null ? stored === 'true' : true;
};

const state = reactive({
  active_caverns_run: false,
  log_entries: [],
  entry_id_counter: 0,
  logs_visible: getInitialLogsVisible(),
});

const caverns_battle_log = ref(null);

function toggleLogsVisibility() {
  state.logs_visible = !state.logs_visible;
  localStorage.setItem(STORAGE_KEY, String(state.logs_visible));
}

function pushEntryToState(entry) {
  if (entry) {
    entry.entry_id = ++state.entry_id_counter;
    state.log_entries.push(entry);
  }
  // evict logs, but leave more space if the user is scrolling (avoid jumping off the mouse location)
  if (state.log_entries.length > (_is_scrolling ? 5000 : 1000)) {
    state.log_entries = state.log_entries.slice(100);
  }
}

onMounted(() => {
  CavernsStore.on(CavernsStore.CAVERNS_BATTLE_INITIALIZED, initialCinchScroll);
  CavernsStore.on(CavernsStore.AI_TURN, onAITurn);
  CavernsStore.on(CavernsStore.BATTLE_EVENT, onBattleEvent);
  CavernsStore.on(CavernsStore.UNIT_CONDITION, onUnitCondition);
  CavernsStore.on(CavernsStore.ABILITY_EXECUTED, onAbilityExecuted);
  CavernsStore.on(CavernsStore.PARTY_DEFEATED, onPartyDefeated);

  _entry_queue = [];

  // update Vue state with message entries from the buffer at an interval
  _entry_queue_interval = setInterval(() => {
    pushEntryToState(_entry_queue.shift());
  }, 50);
});

onBeforeUnmount(() => {
  CavernsStore.removeListener(CavernsStore.CAVERNS_BATTLE_INITIALIZED, initialCinchScroll);
  CavernsStore.removeListener(CavernsStore.AI_TURN, onAITurn);
  CavernsStore.removeListener(CavernsStore.BATTLE_EVENT, onBattleEvent);
  CavernsStore.removeListener(CavernsStore.UNIT_CONDITION, onUnitCondition);
  CavernsStore.removeListener(CavernsStore.ABILITY_EXECUTED, onAbilityExecuted);
  CavernsStore.removeListener(CavernsStore.PARTY_DEFEATED, onPartyDefeated);

  clearInterval(_entry_queue_interval);

  // Clear any pending inspired batches
  for (const batch of _inspired_batches.values()) {
    if (batch.timeoutId) {
      clearTimeout(batch.timeoutId);
    }
  }
  _inspired_batches.clear();

  // Clear any pending heal batches
  for (const batch of _heal_batches.values()) {
    if (batch.timeoutId) {
      clearTimeout(batch.timeoutId);
    }
  }
  _heal_batches.clear();
});

onUpdated(cinchScroll);

function addEntryToQueue(newEntry) {
  _entry_queue.push(newEntry);
}
function cinchScroll() {
  if (!_is_scrolling) {
    caverns_battle_log.value.scrollTop = caverns_battle_log.value.scrollHeight;
  }
}

function initialCinchScroll() {
  setTimeout(cinchScroll, 500);
}

function onAbilityExecuted(data) {
  if (data.abilityHandle === 'multi_shot' && data.phase === 'strikes') return; // for 2-phase multi-shot execution
  const entry = {
    text: getBattleEventLogEntryForAbility(data, CavernsStore.getAll().battleState.allPieces),
  };
  if (document.hidden) {
    pushEntryToState(entry);
  } else {
    addEntryToQueue(entry);
  }
}

function onAITurn(data) {
  if (data.aiUnitId !== _last_ai_unit_id) {
    const ai_unit = data.allUnits[data.aiUnitId];
    if (!ai_unit) return;
    const entry = {
      ...getCavernsNewTurnMessage({ ai_unit }),
      new_turn: true,
    };
    if (document.hidden) {
      pushEntryToState(entry);
    } else {
      addEntryToQueue(entry);
    }
  }
  _last_ai_unit_id = data.aiUnitId;
}

function onBattleEvent(data) {
  const entry = getBattleEventLogEntry({
    ...data,
    allPieces: CavernsStore.getAll().battleState?.allPieces ?? {},
  });
  if (entry) {
    if (document.hidden) {
      pushEntryToState(entry);
      return;
    }
    if (data.event.eventHandle === 'heal') {
      batchHealEvent(data);
    } else {
      TweenMax.delayedCall(0.16, addEntryToQueue, [entry]);
    }
  }
}

function onScroll(event) {
  _is_scrolling = true;
  if (!caverns_battle_log.value) {
    return;
  }
  const { clientHeight, scrollHeight, scrollTop } = caverns_battle_log.value;
  if (Math.abs(scrollTop - scrollHeight) < clientHeight + 25) {
    _is_scrolling = false;
  }
}

function onUnitCondition(data) {
  if (!CavernsStore.getAll().battleState?.allPieces) {
    return;
  }

  const entry = {
    text: getBattleEventLogEntryForCondition(data, CavernsStore.getAll().battleState.allPieces),
  };
  if (!entry) {
    return;
  }

  if (document.hidden) {
    pushEntryToState(entry);
    return;
  }

  // Batch inspired conditions (already handled above when document.hidden)
  if (data.handle === 'inspired') {
    batchInspiredCondition(data);
  } else {
    TweenMax.delayedCall(0.16, addEntryToQueue, [entry]);
  }
}

function onPartyDefeated() {
  setTimeout(() => {
    // flush logs
    state.log_entries.push(..._entry_queue);
    _entry_queue = [];
    // add party defeated log entry
    const entry = browserLanguage.battle_event_log.party_defeated();
    state.log_entries.push({ text: '', new_turn: true });
    state.log_entries.push({ text: '***', color: '#aa0000', new_turn: true });
    state.log_entries.push(entry);
    state.log_entries.push({ text: '***', color: '#aa0000' });
    state.log_entries.push({ text: '', new_tuurn: true });
    state.log_entries.push({ text: '', new_turn: true });
    // stop adding new logs
    clearInterval(_entry_queue_interval);
  }, 200);
}

function batchInspiredCondition(data) {
  const allPieces = CavernsStore.getAll().battleState.allPieces;
  const instigator = allPieces[data.instigatorId];
  const instigatorId = data.instigatorId;
  const instigatorHandle = instigator ? instigator.hero_handle || instigator.handle : null;
  const instigatorName = instigatorHandle ? browserLanguage.unit_names[instigatorHandle] : null;

  const owner = allPieces[data.ownerId];
  const ownerHandle = owner ? owner.hero_handle || owner.handle : null;
  const ownerName = ownerHandle ? browserLanguage.unit_names[ownerHandle] : null;

  if (!instigatorName || !ownerName) {
    return;
  }

  // Get or create batch for this instigator
  let batch = _inspired_batches.get(instigatorId);

  if (!batch) {
    batch = {
      instigatorName,
      ownerNames: [],
      timeoutId: null,
    };
    _inspired_batches.set(instigatorId, batch);
  }

  // Add this owner to the batch
  if (!batch.ownerNames.includes(ownerName)) {
    batch.ownerNames.push(ownerName);
  }

  // Clear existing timeout
  if (batch.timeoutId) {
    clearTimeout(batch.timeoutId);
  }

  // Set new timeout to flush this batch
  batch.timeoutId = setTimeout(() => {
    flushInspiredBatch(instigatorId);
  }, INSPIRED_BATCH_DELAY);
}

function flushInspiredBatch(instigatorId) {
  const batch = _inspired_batches.get(instigatorId);
  if (!batch) {
    return;
  }

  _inspired_batches.delete(instigatorId);

  const { instigatorName, ownerNames } = batch;

  let text;
  if (ownerNames.length === 1) {
    // Single unit - use regular message
    text = browserLanguage.battle_event_log.inspired(instigatorName, ownerNames[0], {});
  } else {
    // Multiple units - use batched message
    text = browserLanguage.battle_event_log.inspired_batch(instigatorName, ownerNames);
  }

  TweenMax.delayedCall(0.16, addEntryToQueue, [{ text, color: '#00ffea' }]);
}

function batchHealEvent(data) {
  const allPieces = CavernsStore.getAll().battleState.allPieces;
  const victim = allPieces[data.event.victimId];
  const victimHandle = victim ? victim.hero_handle || victim.handle : null;
  const victimName = victimHandle ? browserLanguage.unit_names[victimHandle] : null;

  if (!victimName) {
    return;
  }

  const potency = Math.round(data.event.potency);

  // Get or create batch for this potency
  let batch = _heal_batches.get(potency);

  if (!batch) {
    batch = {
      potency,
      victimNames: [],
      timeoutId: null,
    };
    _heal_batches.set(potency, batch);
  }

  // Add this victim to the batch
  if (!batch.victimNames.includes(victimName)) {
    batch.victimNames.push(victimName);
  }

  // Clear existing timeout
  if (batch.timeoutId) {
    clearTimeout(batch.timeoutId);
  }

  // Set new timeout to flush this batch
  batch.timeoutId = setTimeout(() => {
    flushHealBatch(potency);
  }, HEAL_BATCH_DELAY);
}

function flushHealBatch(potency) {
  const batch = _heal_batches.get(potency);
  if (!batch) {
    return;
  }

  _heal_batches.delete(potency);

  const { victimNames } = batch;

  let entry;
  if (victimNames.length === 1) {
    // Single unit - use regular message
    entry = browserLanguage.battle_event_log.heal(null, victimNames[0], {
      event: { potency },
    });
  } else {
    // Multiple units - use batched message
    entry = browserLanguage.battle_event_log.heal_batch(victimNames, potency);
  }

  TweenMax.delayedCall(0.16, addEntryToQueue, [entry]);
}
</script>
